import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { documentApi } from "api/document.api";
import { DocumentModal } from "./components/DocumentModal";
import { useProject } from "hooks/useProject";
import { useDebounce } from "hooks/useDebounce";
import { Document } from "types/document";
import { checkRole } from "utils/auth";
import { userStore } from "store/userStore";

const deleteDocumentPath = "xoa-tai-lieu";
export const downloadDocumentPath = "download-tai-lieu";
const editDocumentPath = "chinh-sua-tai-lieu";

export const DocumentPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Document[]>([]);
  const [total, setTotal] = useState(0);
  const [projectId, setProjectId] = useState<string | number>("");

  const documentModalRef = useRef<DocumentModal>(null);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const { Projects, totalProject, fetchProject, loadingProject, setQueryProject, queryProject } = useProject({
    initQuery: {
      page: 1,
      limit: 50,
      search: "",
    },
  });

  useEffect(() => {
    fetchProject();
  }, [queryProject]);

  const debouncedSearch = useDebounce(search, 300);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await documentApi.findAll(queryParams);
      setData(res.data.documents);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (row: Document) => {
      setLoading(true);
      try {
        await documentApi.delete(row.id);
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData(query);
  }, [query]);

  const canEditDocument = checkRole(editDocumentPath, userStore.info.role?.permissions);
  const canDownloadDocument = checkRole(downloadDocumentPath, userStore.info.role?.permissions);
  const canDeleteDocument = checkRole(deleteDocumentPath, userStore.info.role?.permissions);

  const columns = useMemo<TableColumnsType<Document>>(
    () => [
      {
        title: "Tên tài liệu",
        dataIndex: "name",
        key: "name",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Dự án",
        dataIndex: "project",
        key: "project",
        render: (_, record: Document) => <TableContent>{record.project?.name || "--"}</TableContent>,
      },
      {
        title: "File tài liệu",
        key: "role",
        width: 260,
        render: (_, record: Document) => (
          <div
            className="!text-[#1fa6aa] hover:text-[#1fa6aa] flex items-center gap-2 !p-0"
            onClick={() => canDownloadDocument && downloadFile(record)}
            style={{ cursor: "pointer" }}
          >
            <FileOutlined style={{ cursor: "pointer" }} /> {record.fileName}
          </div>
        ),
      },
      {
        title: "Nhân viên được xem",
        key: "staffs",
        render: (_, record: Document) => (
          <TableContent>
            {record.staffs?.length
              ? record.staffs.map((staff) => staff.name || staff.username).join(", ")
              : "--"}
          </TableContent>
        ),
      },
      ...(canEditDocument || canDeleteDocument
        ? [
            {
              title: "Thao tác",
              key: "action",
              width: 160,
              align: "center" as const,
              render: (_: any, record: Document) => (
                <div className="flex gap-2 justify-center items-center">
                  {canEditDocument && (
                    <Tooltip title="Cập nhật">
                      <ActionButton
                        aria-label="Cập nhật"
                        onClick={() => {
                          documentModalRef.current?.handleUpdate(record);
                        }}
                      >
                        <EditOutlined style={{ color: "#6c84e5" }} />
                      </ActionButton>
                    </Tooltip>
                  )}
                  {canDeleteDocument && (
                    <Tooltip title="Xóa">
                      <Popconfirm
                        cancelText="Không"
                        okText="Xóa"
                        onConfirm={() => handleDelete(record)}
                        title="Xác nhận xóa?"
                      >
                        <ActionButton aria-label="Xóa">
                          <DeleteOutlined style={{ color: "#ef4444" }} />
                        </ActionButton>
                      </Popconfirm>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [handleDelete, canEditDocument, canDeleteDocument, canDownloadDocument],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  const handleProjectChange = useCallback((value: string | number | undefined) => {
    setProjectId(value ?? "");
    setQuery((prev) => ({
      ...prev,
      page: 1,
      projectId: value ? Number(value) : null,
    }));
  }, []);

  const downloadFile = useCallback((file: Document) => {
    window.open(file.filePath, "_blank");
  }, []);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm theo tên tài liệu",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            filters={[
              {
                key: "projectId",
                value: projectId,
                placeholder: "Dự án",
                width: 200,
                options: Projects?.map((project) => ({ value: project.id, label: project.name })),
                allowClear: true,
                // showSearch: true,
                filterOption: false,
                onChange: handleProjectChange,
              },
            ]}
            leftActions={
              canEditDocument && (
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    documentModalRef.current?.handleCreate();
                  }}
                >
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Document>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            scroll={{ x: 1100 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              setQuery((prev) => ({ ...prev, page, limit }));
            }}
          />
        }
      />

      <DocumentModal ref={documentModalRef} onSubmitOk={() => fetchData(query)} onClose={() => {}} />
    </div>
  );
};
